// Khung chuyển khoản tiền ship cho đơn "Shop trả" (docs/KE_HOACH_SHOP_TRA_SHIP_QR.md §2.8):
// QR động (đúng số tiền + nội dung "SHIP <mã đơn>"), thông tin tài khoản của NPP, trạng thái duyệt.
// Đơn chỉ được gửi sang BEST sau khi NPP (Sunrise: "ShopTrack") xác nhận đã nhận tiền.

import { useState, type ReactNode } from "react"
import { Link } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { toast } from "sonner"
import { Check, CheckCircle2, Copy, HandCoins, Hourglass, Layers, XCircle } from "lucide-react"
import { get } from "@/lib/api"
import { money } from "@/lib/format"
import { cn } from "@/lib/utils"
import { useNppName } from "@/components/layout/shell-model"

/** Lệnh chuyển gộp chứa đơn (shop chọn nhiều đơn, chuyển một lần). */
export interface ShipBatchRef {
  id: string
  status: "pending" | "approved" | "debt" | "rejected" | "cancelled"
  amount: number
  orderCount: number
  transferContent: string
}

interface ShipPayment {
  batch: ShipBatchRef | null
  status: "pending" | "approved" | "debt" | "rejected" | "cancelled"
  amount: number
  transferContent: string
  bankName: string | null
  accountNumber: string | null
  accountName: string | null
  qrDataUrl: string | null
  decisionNote: string | null
  decidedAt: string | null
  createdAt: string
}

export function CopyValue({ value, what }: { value: string; what: string }) {
  const [done, setDone] = useState(false)
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value)
          setDone(true)
          toast.success(`Đã chép ${what}`)
          window.setTimeout(() => setDone(false), 1500)
        } catch {
          toast.error("Không chép được")
        }
      }}
      className="inline-flex min-h-9 items-center gap-1 rounded-md px-2 text-xs font-medium text-accent hover:bg-accent/10"
      aria-label={`Chép ${what}`}
    >
      {done ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
      Chép
    </button>
  )
}

export function ShipPaymentPanel({ orderId, ownerShopId, orderStatus }: { orderId: string; ownerShopId: string | null; orderStatus: string }) {
  // Cả cây đơn vị cùng một NPP — người xem (kể cả cấp trên của chủ đơn) thấy đúng tên bên nhận tiền
  const nppName = useNppName()
  const q = useQuery({
    queryKey: ["ship-payment", orderId, ownerShopId],
    queryFn: () => get<{ payment: ShipPayment }>(`/orders/${encodeURIComponent(orderId)}/ship-payment${ownerShopId ? `?shopId=${encodeURIComponent(ownerShopId)}` : ""}`),
    retry: false,
    // Đang chờ duyệt: hỏi lại để thấy ngay lúc NPP xác nhận
    refetchInterval: (query) => (query.state.data?.payment.status === "pending" ? 15_000 : false),
  })
  const p = q.data?.payment
  if (!p) return null

  if (p.status === "debt") {
    return (
      <div className="mb-6 flex items-start gap-2 rounded-lg border border-warn/40 bg-warn/10 px-3 py-2.5 text-sm text-ink">
        <HandCoins className="mt-0.5 size-4 shrink-0 text-warn" aria-hidden />
        <span>
          {nppName} cho nợ <b>{money(p.amount)}</b> tiền ship — đơn đã được gửi sang hãng vận chuyển.
        </span>
      </div>
    )
  }
  if (p.status === "pending" && p.batch) {
    return (
      <div className="mb-6 flex flex-wrap items-start gap-2 rounded-lg border border-accent/40 bg-accent/5 px-3 py-2.5 text-sm text-ink">
        <Layers className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
        <span className="min-w-0 flex-1">
          Đơn nằm trong lệnh chuyển gộp <code className="font-mono font-semibold">{p.batch.transferContent}</code> ({p.batch.orderCount} đơn · {money(p.batch.amount)}).{" "}
          {p.batch.status === "pending" ? "Chuyển khoản theo QR của lệnh — đừng chuyển riêng đơn này." : `${nppName} đang xử lý lệnh này.`}
        </span>
        <Link to={`/ship-payments/${encodeURIComponent(p.batch.id)}${ownerShopId ? `?shopId=${encodeURIComponent(ownerShopId)}` : ""}`} className="shrink-0 text-sm font-medium text-accent hover:underline">
          Mở lệnh →
        </Link>
      </div>
    )
  }
  if (p.status === "approved") {
    return (
      <div className="mb-6 flex items-start gap-2 rounded-lg border border-ok/30 bg-ok/10 px-3 py-2.5 text-sm text-ink">
        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-ok" aria-hidden />
        <span>
          {nppName} đã nhận <b>{money(p.amount)}</b> tiền ship — đơn đã được gửi sang hãng vận chuyển.
        </span>
      </div>
    )
  }
  if (p.status === "rejected" || p.status === "cancelled") {
    return (
      <div className="mb-6 flex items-start gap-2 rounded-lg border border-rule bg-paperSoft px-3 py-2.5 text-sm text-ink">
        <XCircle className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
        <span>
          {p.status === "rejected" ? (
            <>
              {nppName} chưa xác nhận tiền ship {money(p.amount)}
              {p.decisionNote ? `: ${p.decisionNote}` : ""}. Đơn đã hủy, tồn kho đã trả lại.
            </>
          ) : (
            <>Đơn đã hủy trước khi {nppName} xác nhận tiền ship.</>
          )}
        </span>
      </div>
    )
  }

  return (
    <QrTransferBox
      title="Chuyển khoản tiền ship"
      amount={p.amount}
      transferContent={p.transferContent}
      qrDataUrl={p.qrDataUrl}
      bankName={p.bankName}
      accountNumber={p.accountNumber}
      accountName={p.accountName}
      highlight={orderStatus === "cho-duyet"}
      footer={
        <>
          Giữ <b>đúng nội dung</b> khi chuyển.
        </>
      }
    />
  )
}

/** Khung QR chuyển khoản: ảnh QR + số tiền / nội dung / tài khoản (dùng cho từng đơn và lệnh gộp). */
export function QrTransferBox(props: {
  title: string
  amount: number
  transferContent: string
  qrDataUrl: string | null
  bankName: string | null
  accountNumber: string | null
  accountName: string | null
  highlight: boolean
  footer: ReactNode
}) {
  const p = props
  return (
    <section
      aria-labelledby="ship-payment-title"
      className="mb-6 overflow-hidden rounded-xl border-2 border-accent/40 bg-raise shadow-sm"
    >
      <div className="flex items-center gap-2 border-b border-rule bg-accent/5 px-4 py-3">
        <Hourglass className="size-4 shrink-0 text-accent" aria-hidden />
        <h2 id="ship-payment-title" className="text-sm font-semibold text-ink">
          {p.title}
        </h2>
      </div>
      <div className="grid gap-4 p-4 sm:grid-cols-[220px_minmax(0,1fr)]">
        <div className="mx-auto w-full max-w-[240px]">
          {p.qrDataUrl ? (
            <img src={p.qrDataUrl} alt={`Mã QR chuyển ${money(p.amount)}, nội dung ${p.transferContent}`} className="w-full rounded-lg border border-rule bg-white p-1" />
          ) : (
            <div className="grid aspect-square place-items-center rounded-lg border border-dashed border-rule p-4 text-center text-xs text-muted">
              Chưa tạo được mã QR
            </div>
          )}
        </div>
        <div className="min-w-0 space-y-3 text-sm">
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2">
            <dt className="text-muted">Số tiền</dt>
            <dd className="flex items-center justify-between gap-2">
              <span className="font-mono text-lg font-semibold text-accent tabular-nums">{money(p.amount)}</span>
              <CopyValue value={String(p.amount)} what="số tiền" />
            </dd>
            <dt className="text-muted">Nội dung</dt>
            <dd className="flex min-w-0 items-center justify-between gap-2">
              <code className="truncate rounded bg-paperSoft px-1.5 py-0.5 font-mono text-sm font-semibold text-ink">{p.transferContent}</code>
              <CopyValue value={p.transferContent} what="nội dung chuyển khoản" />
            </dd>
            {p.bankName && (
              <>
                <dt className="text-muted">Ngân hàng</dt>
                <dd className="text-ink">{p.bankName}</dd>
              </>
            )}
            {p.accountNumber && (
              <>
                <dt className="text-muted">Số TK</dt>
                <dd className="flex items-center justify-between gap-2">
                  <span className="font-mono text-ink">{p.accountNumber}</span>
                  <CopyValue value={p.accountNumber} what="số tài khoản" />
                </dd>
              </>
            )}
            {p.accountName && (
              <>
                <dt className="text-muted">Chủ TK</dt>
                <dd className="text-ink">{p.accountName}</dd>
              </>
            )}
          </dl>
          <p className={cn("rounded-md px-3 py-2 text-xs", p.highlight ? "bg-warn/10 text-ink" : "bg-paperSoft text-inkSoft")}>{p.footer}</p>
        </div>
      </div>
    </section>
  )
}
